import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, Shield, Mail, Phone, ExternalLink, Plus, Trash2, Briefcase,
  CheckCircle2, AlertTriangle, FileCheck,
} from "lucide-react";

export default function MasterInsuranceDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [adj, setAdj] = useState({ name: "", email: "", phone: "", region: "" });

  const load = () => api.get(`/master/insurance/${id}`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [id]);

  const addAdjuster = async () => {
    if (!adj.name) return toast.error("Adjuster name required");
    await api.post(`/master/insurance/${id}/adjusters`, adj);
    toast.success("Adjuster added");
    setAdj({ name: "", email: "", phone: "", region: "" });
    load();
  };

  const removeAdjuster = async (adjId) => {
    await api.delete(`/master/insurance/${id}/adjusters/${adjId}`);
    load();
  };

  if (!data) return <div className="text-slate-500">Loading…</div>;
  const ins = data.insurance;

  return (
    <div className="space-y-6 animate-slide-up" data-testid="ins-detail-page">
      <Link to="/master/insurance" className="text-xs text-slate-500 hover:text-blue-400 flex items-center gap-1 w-fit"><ArrowLeft className="w-3 h-3" /> Back to Insurance Companies</Link>

      <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-[#0B1120] to-[#0A0F1C] p-6">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-cyan-600/15 border border-cyan-500/20 flex items-center justify-center">
            <Shield className="w-7 h-7 text-cyan-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-3xl font-semibold tracking-tight">{ins.name}</h1>
            <div className="text-sm text-slate-400 mt-1">Short code: <span className="font-mono">{ins.short_code || "—"}</span></div>
            <div className="flex flex-wrap gap-3 mt-3 text-xs text-slate-500">
              {ins.claims_email && <span className="flex items-center gap-1.5"><Mail className="w-3 h-3" />{ins.claims_email}</span>}
              {ins.phone && <span className="flex items-center gap-1.5 font-mono"><Phone className="w-3 h-3" />{ins.phone}</span>}
              {ins.submission_portal && <a href={ins.submission_portal} target="_blank" rel="noreferrer" className="text-blue-400 flex items-center gap-1.5"><ExternalLink className="w-3 h-3" />Portal</a>}
            </div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
        <PolicyCard label="Payment Terms" value={ins.payment_terms} />
        <PolicyCard label="Scan Policy" value={ins.scan_policy} variant={ins.scan_policy === "always" ? "good" : ins.scan_policy === "never" ? "bad" : "warn"} />
        <PolicyCard label="OEM Glass" value={ins.oem_glass_policy} variant={ins.oem_glass_policy === "always" ? "good" : ins.oem_glass_policy === "never" ? "bad" : "warn"} />
        <PolicyCard label="Approval Days" value={`${ins.typical_approval_days || "—"} days`} />
        <PolicyCard label="OEM Proof Required" value={ins.requires_oem_proof ? "Yes" : "No"} variant={ins.requires_oem_proof ? "warn" : "good"} />
        <PolicyCard label="Dual Cal Approved" value={ins.approves_dual_calibration ? "Yes" : "No"} variant={ins.approves_dual_calibration ? "good" : "bad"} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Adjusters · {(ins.adjusters || []).length}</div>
          </div>
          <div className="space-y-2 mb-4">
            {(ins.adjusters || []).map((a) => (
              <div key={a.id} data-testid={`adj-${a.id}`} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-sm font-medium">{a.name}</div>
                    {a.region && <div className="text-[10px] uppercase tracking-wide text-slate-500 mt-0.5">{a.region}</div>}
                    <div className="text-xs text-slate-400 mt-1 space-x-3">
                      {a.email && <span>📧 {a.email}</span>}
                      {a.phone && <span className="font-mono">📞 {a.phone}</span>}
                    </div>
                  </div>
                  <button data-testid={`adj-remove-${a.id}`} onClick={() => removeAdjuster(a.id)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            ))}
            {(ins.adjusters || []).length === 0 && <div className="text-center text-slate-500 text-xs py-4">No adjusters yet</div>}
          </div>
          <div className="border-t border-slate-800 pt-3 space-y-2">
            <Input data-testid="adj-name" placeholder="Adjuster name" value={adj.name} onChange={(e) => setAdj({ ...adj, name: e.target.value })} className="bg-[#030712] border-slate-800" />
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="adj-email" placeholder="Email" value={adj.email} onChange={(e) => setAdj({ ...adj, email: e.target.value })} className="bg-[#030712] border-slate-800" />
              <Input data-testid="adj-phone" placeholder="Phone" value={adj.phone} onChange={(e) => setAdj({ ...adj, phone: e.target.value })} className="bg-[#030712] border-slate-800" />
            </div>
            <Input data-testid="adj-region" placeholder="Region (optional)" value={adj.region} onChange={(e) => setAdj({ ...adj, region: e.target.value })} className="bg-[#030712] border-slate-800" />
            <Button data-testid="adj-add-btn" onClick={addAdjuster} className="w-full bg-blue-600 hover:bg-blue-500"><Plus className="w-3 h-3 mr-1.5" />Add Adjuster</Button>
          </div>
        </div>

        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3 flex items-center gap-1.5"><Briefcase className="w-3 h-3" />Recent Jobs · {data.jobs.length}</div>
          {data.jobs.length === 0 ? (
            <div className="text-center text-slate-500 py-8 text-sm">No jobs yet for this carrier</div>
          ) : (
            <div className="divide-y divide-slate-800/60">
              {data.jobs.slice(0, 10).map((j) => (
                <Link key={j.id} to={`/jobs/${j.id}`} className="flex items-center gap-3 py-2 hover:bg-slate-900/30 px-2 rounded-lg transition-colors">
                  <div className="font-mono text-xs text-cyan-300 w-32 truncate">{j.vin}</div>
                  <div className="flex-1 text-sm truncate">{j.customer_name}</div>
                  <Badge className="bg-slate-800 border-slate-700 text-slate-300 text-[10px]">{j.status}</Badge>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {ins.preferred_documentation && (
        <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-blue-300 mb-2 flex items-center gap-1.5"><FileCheck className="w-3 h-3" />Preferred Documentation</div>
          <div className="text-sm text-slate-300 whitespace-pre-line">{ins.preferred_documentation}</div>
        </div>
      )}
    </div>
  );
}

const PolicyCard = ({ label, value, variant }) => {
  const v = { good: "border-emerald-500/30 text-emerald-300", bad: "border-red-500/30 text-red-300", warn: "border-amber-500/30 text-amber-300" }[variant] || "border-slate-800 text-slate-300";
  const Icon = variant === "good" ? CheckCircle2 : variant === "bad" ? AlertTriangle : null;
  return (
    <div className={`rounded-xl border bg-[#0B1120] p-4 ${v}`}>
      <div className="text-[9px] uppercase tracking-wider font-bold text-slate-500 mb-1">{label}</div>
      <div className="text-sm font-medium flex items-center gap-1.5 capitalize">{Icon && <Icon className="w-3.5 h-3.5" />}{(value || "—").toString().replace("_", " ")}</div>
    </div>
  );
};
